import React from 'react';
import SectionHeader from './SectionHeader';
import TextInput from './TextInput';
import SelectInput from './SelectInput';
import AddressFields from './AddressFields';
import NotesInput from './NotesInput';
import CheckboxInput from './CheckboxInput';
import { TCP_RELATIONSHIPS } from './options';
import { formatPhone } from './format';
import { secondaryTcpStarted } from './validation';
import { SECTION_DEFAULTS, useSectionDefaults } from './defaults';

/**
 * One trusted contact's fields. Rendered twice: once for the required TCP and
 * once for the optional secondary, which lives in the same step rather than a
 * new one — `current_step` is a persisted integer and `PDS_TOTAL_STEPS` is a
 * static value on the backend, so the step list must not grow.
 */
function TcpFields({ idPrefix, data, onChange, required, homeAddress }) {
  const d = data || {};
  const update = (field) => (value) => onChange({ ...d, [field]: value });

  return (
    <>
      <div className="row g-3">
        <TextInput
          id={`${idPrefix}-name`}
          label="Trusted Contact's full name"
          col="col-md-6"
          required={required}
          value={d.full_name}
          onChange={update('full_name')}
          autoComplete="off"
        />
        <SelectInput
          id={`${idPrefix}-relationship`}
          label="Their relationship to you"
          col="col-md-6"
          required={required}
          options={TCP_RELATIONSHIPS}
          value={d.relationship}
          onChange={update('relationship')}
        />
        <TextInput
          id={`${idPrefix}-phone`}
          label="Their phone number"
          type="tel"
          col="col-md-6"
          required={required}
          formatter={formatPhone}
          value={d.phone}
          onChange={update('phone')}
          autoComplete="off"
        />
        <TextInput
          id={`${idPrefix}-email`}
          label="Their email address"
          type="email"
          col="col-md-6"
          required={required}
          value={d.email}
          onChange={update('email')}
          autoComplete="off"
        />
      </div>

      <div className="mt-4">
        <AddressFields
          idPrefix={`${idPrefix}-addr`}
          legend="Their physical address"
          required={required}
          disallowPoBox
          sameAsAddress={homeAddress}
          sameAsLabel="Same mailing address as mine"
          value={d.address}
          onChange={update('address')}
        />
      </div>
    </>
  );
}

/**
 * Trusted Contact Person — required by FINRA Rule 4512 and by both of our
 * custodians. A PO Box is not accepted for the TCP address.
 *
 * A second TCP is opt-in. The CRM tracks up to two, but we only ask for one
 * by default; ideally it is someone other than a spouse, though that is not a
 * requirement. Saying yes makes every field required — a half-named trusted
 * contact is not one the custodian can act on.
 */
export default function StepTrustedContact({
  number, person = 'primary', data, onChange, homeAddress,
}) {
  useSectionDefaults(data, onChange, SECTION_DEFAULTS.trusted_contact);

  const d = data || {};
  const isSpouse = person === 'spouse';
  const p = isSpouse ? 'spouse_tcp' : 'tcp';
  const who = isSpouse ? "Your Spouse/Partner's" : 'Your';
  const update = (field) => (value) => onChange({ ...d, [field]: value });

  // Fall back to the data itself so a record saved before this checkbox
  // existed still shows the secondary contact it already holds.
  const wantsSecondary = d.wants_secondary_tcp === undefined
    ? secondaryTcpStarted(d.secondary)
    : d.wants_secondary_tcp === true;

  // Clearing on "no" keeps the stored form honest: a secondary contact the
  // client backed out of must not reach Client Services or the PDF.
  const toggleSecondary = (checked) => onChange({
    ...d,
    wants_secondary_tcp: checked,
    ...(checked ? {} : { secondary: {} }),
  });

  return (
    <div>
      <SectionHeader number={number} title={`${who} Trusted Contact Person`} />

      <div className="alert alert-light border-start border-4 border-danger">
        <p className="fw-semibold mb-2">
          We require that you designate a Trusted Contact Person (TCP).
        </p>
        <p className="mb-0 small">
          A TCP is someone we may contact if we have concerns about your
          health, well-being, or welfare, or if we are unable to reach you
          regarding important account matters. Your TCP must be at least 18
          years old, and they will <strong>not</strong> have authority over
          your accounts or access to your account information.
        </p>
      </div>

      <TcpFields
        idPrefix={p}
        data={d}
        onChange={onChange}
        required
        homeAddress={homeAddress}
      />

      <hr className="my-4" />

      <div className="cashflow-subsection-title mb-2">
        Would you like to add a secondary Trusted Contact Person?
      </div>
      <p className="text-muted small">
        This is optional. Ideally a second Trusted Contact is someone other
        than your spouse or partner, but that is not a requirement.
      </p>

      <CheckboxInput
        id={`${p}-wants-secondary`}
        label="Yes, I would like to name a second Trusted Contact Person."
        checked={wantsSecondary}
        onChange={toggleSecondary}
      />

      {wantsSecondary && (
        <div className="mt-3">
          <TcpFields
            idPrefix={`${p}-secondary`}
            data={d.secondary}
            onChange={update('secondary')}
            required
            homeAddress={homeAddress}
          />
        </div>
      )}

      <NotesInput
        value={d.notes}
        onChange={update('notes')}
        label="Anything else we should know about this section?"
        placeholder="Optional"
      />
    </div>
  );
}
